/**
 * Solar PV System Calculation Engine
 * Inspired by PVSyst methodology
 * 
 * Implements industry-standard calculations for:
 * - Solar irradiation transposition (Perez model)
 * - Temperature effects on module performance
 * - System losses (array, inverter, wiring)
 * - Monthly and annual energy production
 * - Performance ratio calculations
 */

import type { CityWeatherData } from '../data/saudiWeatherData';
import { getWeatherData, type WeatherData } from './weatherDataService';
import { validateCalculationInputs } from './inputValidationService';

/** Convert WeatherData (kWh/m²/day) to CityWeatherData (kWh/m²/month) for the engine */
function weatherDataToCityWeatherData(data: WeatherData, latitude: number, longitude: number): CityWeatherData {
  if (!Array.isArray(data.monthlyWindSpeed) || data.monthlyWindSpeed.length !== 12) {
    throw new Error('NREL weather data missing monthlyWindSpeed values for one or more months.');
  }

  if (data.monthlyWindSpeed.some((value) => !Number.isFinite(value))) {
    throw new Error('NREL weather data contains non-finite monthlyWindSpeed values.');
  }

  return {
    id: 'fetched',
    name: `${latitude.toFixed(2)}°, ${longitude.toFixed(2)}°`,
    name_ar: '',
    latitude: data.latitude,
    longitude: data.longitude,
    altitude: 0,
    monthly: {
      ghi: data.monthlyGHI.map((ghiDay, i) => ghiDay * DAYS_IN_MONTH[i]),
      temp: [...data.monthlyTemp],
      windSpeed: [...data.monthlyWindSpeed],
    },
  };
}

export interface CalculationInputs {
  // Location
  cityWeatherData?: CityWeatherData; // Optional - will be fetched if not provided
  latitude: number;
  longitude: number;
  altitude: number;
  
  // System configuration
  systemSize: number;        // kWp
  tilt: number;             // degrees
  azimuth: number;          // degrees (180 = south)
  mountingType: string;
  surfaceType: string;
  
  // Consumption
  monthlyConsumption?: number[];  // kWh per month (12 values)
  yearlyConsumption?: number;     // kWh per year
  
  // Optional
  shadingLevel?: 'none' | 'partial' | 'significant';
  modulePower?: number;     // Watts (default: 550W)
  soilingLoss?: number;     // 0-1 (default: auto-calculated based on user type)
  userType?: 'residential' | 'commercial' | 'industrial' | 'agricultural';
  inverterSizeOverrideKw?: number; // Optional AC inverter size override for clipping logic
  panelCountOverride?: number; // Optional explicit module count override
}

export interface MonthlyResult {
  month: number;
  monthName: string;
  ghi: number;              // kWh/m²
  temp: number;             // °C
  poa: number;              // Plane of Array irradiation (kWh/m²)
  arrayOutput: number;      // kWh (DC)
  gridOutput: number;       // kWh (AC)
  performanceRatio: number; // %
}

export interface CalculationResults {
  // System configuration
  systemSize: number;
  panelCount: number;
  modulePower: number;
  inverterSize: number;
  
  // Annual results
  annualProduction: number;      // kWh/year
  specificProduction: number;    // kWh/kWp/year
  performanceRatio: number;      // %
  capacityFactor: number;        // %
  
  // Monthly breakdown
  monthlyResults: MonthlyResult[];
  
  // Losses breakdown
  losses: {
    arrayLosses: number;         // kWh
    arrayLossesPercent: number;  // %
    inverterLosses: number;      // kWh
    inverterLossesPercent: number; // %
    totalLosses: number;         // kWh
    totalLossesPercent: number;  // %
  };
  
  // Energy flow
  energyFlow: {
    solarIrradiation: number;    // kWh
    arrayOutput: number;         // kWh (DC)
    gridOutput: number;          // kWh (AC)
  };
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Main calculation function (async version with NREL-only weather support)
 */
export async function calculateSolarSystem(inputs: CalculationInputs): Promise<CalculationResults> {
  // Validate inputs
  const validation = validateCalculationInputs(inputs);
  if (!validation.isValid) {
    throw new Error(`Invalid calculation inputs: ${validation.errors.join(', ')}`);
  }

  // Get weather data (use provided or fetch from hybrid service)
  let cityWeatherData: CityWeatherData;
  if (inputs.cityWeatherData) {
    cityWeatherData = inputs.cityWeatherData;
    console.log('[solarCalculationEngine] Weather: using provided cityWeatherData', {
      location: cityWeatherData.name || cityWeatherData.id,
      annualGHI: cityWeatherData.monthly.ghi.reduce((s, v) => s + v, 0).toFixed(0),
      tempRange: `${Math.min(...cityWeatherData.monthly.temp).toFixed(1)}–${Math.max(...cityWeatherData.monthly.temp).toFixed(1)}°C`,
    });
  } else {
    const result = await getWeatherData({
      latitude: inputs.latitude,
      longitude: inputs.longitude,
    });
    cityWeatherData = weatherDataToCityWeatherData(result.data, inputs.latitude, inputs.longitude);
    console.log('[solarCalculationEngine] Weather: fetched for coordinates', {
      lat: inputs.latitude,
      lon: inputs.longitude,
      source: result.source.source,
      sourceMessage: result.source.message,
      annualGHI: cityWeatherData.monthly.ghi.reduce((s, v) => s + v, 0).toFixed(0),
      tempRange: `${Math.min(...cityWeatherData.monthly.temp).toFixed(1)}–${Math.max(...cityWeatherData.monthly.temp).toFixed(1)}°C`,
    });
  }
  const modulePower = inputs.modulePower || 550; // Default 550W modules
  const panelCountOverride =
    typeof inputs.panelCountOverride === 'number' && Number.isFinite(inputs.panelCountOverride)
      ? Math.floor(inputs.panelCountOverride)
      : 0;
  const panelCount = panelCountOverride > 0
    ? panelCountOverride
    : Math.ceil((inputs.systemSize * 1000) / modulePower);
  const actualSystemSize = (panelCount * modulePower) / 1000; // Recalculate based on whole panels
  
  // Inverter sizing (DC:AC ratio of 1.3)
  const inverterSizeOverrideKw =
    typeof inputs.inverterSizeOverrideKw === 'number' && Number.isFinite(inputs.inverterSizeOverrideKw)
      ? inputs.inverterSizeOverrideKw
      : 0;
  const inverterSize = inverterSizeOverrideKw > 0 ? inverterSizeOverrideKw : actualSystemSize / 1.3;
  
  // Get shading factor
  const shadingFactor = getShadingFactor(inputs.shadingLevel || 'none');

  console.log('[solarCalculationEngine] Weather usage in calculations: for each month we use GHI (kWh/m²) → transposition → POA; temp (°C) → cell temperature → array output (with losses). Then array → inverter → grid output.');

  // Calculate monthly results
  const monthlyResults: MonthlyResult[] = [];
  let totalArrayOutput = 0;
  let totalGridOutput = 0;
  let totalPOA = 0;

  for (let month = 0; month < 12; month++) {
    const ghi = cityWeatherData.monthly.ghi[month];
    const temp = cityWeatherData.monthly.temp[month];
    const windSpeed = cityWeatherData.monthly.windSpeed[month];
    if (!Number.isFinite(windSpeed)) {
      throw new Error(`Missing wind speed data for month ${month + 1}.`);
    }
    const daysInMonth = DAYS_IN_MONTH[month];
    
    // 1. Transposition: GHI to POA (Plane of Array)
    const poa = transposeIrradiation(
      ghi,
      inputs.latitude,
      inputs.tilt,
      inputs.azimuth,
      month
    );
    
    // 2. Apply shading
    const poaAfterShading = poa * shadingFactor;
    
    // 3. Calculate DC array output
    const arrayOutput = calculateArrayOutput(
      poaAfterShading,
      actualSystemSize,
      temp,
      windSpeed,
      inputs.mountingType,
      inputs.surfaceType,
      daysInMonth,
      inputs
    );
    
    // 4. Calculate AC grid output (after inverter)
    const gridOutput = calculateGridOutput(arrayOutput, inverterSize);
    
    // 5. Calculate performance ratio (PVSyst methodology)
    // PR = Actual Energy / Theoretical STC Energy
    // Note: `poaAfterShading` is in kWh/m²/month, and kWp is defined at 1 kW/m² (STC),
    // so the theoretical STC energy is simply POA(kWh/m²) × systemSize(kWp).
    // (No /1000 here; dividing would shrink the denominator by 1000 and inflate PR by 1000×.)
    const theoreticalProductionSTC = poaAfterShading * actualSystemSize;
    const performanceRatio = theoreticalProductionSTC > 0 ? (gridOutput / theoreticalProductionSTC) * 100 : 0;
    
    monthlyResults.push({
      month: month + 1,
      monthName: MONTH_NAMES[month],
      ghi,
      temp,
      poa: poaAfterShading,
      arrayOutput,
      gridOutput,
      performanceRatio
    });
    
    totalArrayOutput += arrayOutput;
    totalGridOutput += gridOutput;
    totalPOA += poaAfterShading;
  }

  console.log('[solarCalculationEngine] Weather-driven results', {
    totalPOAKwhM2: totalPOA.toFixed(1),
    annualProductionKwh: totalGridOutput.toFixed(0),
    theoreticalSTC: (totalPOA * actualSystemSize).toFixed(0),
  });

  // Calculate annual metrics
  const annualProduction = totalGridOutput;
  const specificProduction = annualProduction / actualSystemSize;
  
  // Performance Ratio (PVSyst methodology - normalized to STC)
  // PR = Actual Annual Energy / (Total POA Irradiance × System Size)
  // `totalPOA` is kWh/m²/year-equivalent (sum of monthly kWh/m²), so no /1000.
  const theoreticalAnnualProductionSTC = totalPOA * actualSystemSize;
  const performanceRatio = theoreticalAnnualProductionSTC > 0 ? (annualProduction / theoreticalAnnualProductionSTC) * 100 : 0;
  
  // Capacity Factor = Actual Energy / (System Size * 8760 hours)
  const capacityFactor = (annualProduction / (actualSystemSize * 8760)) * 100;
  
  // Calculate losses
  const solarIrradiation = totalPOA * actualSystemSize;
  const arrayLosses = solarIrradiation - totalArrayOutput;
  const inverterLosses = totalArrayOutput - totalGridOutput;
  const totalLosses = solarIrradiation - totalGridOutput;
  
  return {
    systemSize: actualSystemSize,
    panelCount,
    modulePower,
    inverterSize,
    annualProduction,
    specificProduction,
    performanceRatio,
    capacityFactor,
    monthlyResults,
    losses: {
      arrayLosses,
      arrayLossesPercent: (arrayLosses / solarIrradiation) * 100,
      inverterLosses,
      inverterLossesPercent: (inverterLosses / solarIrradiation) * 100,
      totalLosses,
      totalLossesPercent: (totalLosses / solarIrradiation) * 100
    },
    energyFlow: {
      solarIrradiation,
      arrayOutput: totalArrayOutput,
      gridOutput: totalGridOutput
    }
  };
}

/**
 * Transpose GHI to POA using simplified Perez model
 */
function transposeIrradiation(
  ghi: number,
  latitude: number,
  tilt: number,
  azimuth: number,
  month: number
): number {
  // Simplified transposition model for Saudi Arabia
  // Accounts for tilt and orientation
  
  const latRad = (latitude * Math.PI) / 180;
  const tiltRad = (tilt * Math.PI) / 180;
  
  // Solar declination for mid-month
  const dayOfYear = month * 30.5 + 15;
  const declination = 23.45 * Math.sin((360 / 365) * (dayOfYear - 81) * Math.PI / 180);
  const declRad = (declination * Math.PI) / 180;
  
  // Calculate tilt factor
  const cosTilt = Math.cos(tiltRad);
  const sinTilt = Math.sin(tiltRad);
  const cosLat = Math.cos(latRad);
  const sinLat = Math.sin(latRad);
  const cosDecl = Math.cos(declRad);
  const sinDecl = Math.sin(declRad);
  
  // Rb: ratio of beam radiation on tilted surface to horizontal
  const cosTheta = sinLat * sinDecl * cosTilt 
                 - cosLat * sinDecl * sinTilt * Math.cos((azimuth - 180) * Math.PI / 180)
                 + cosLat * cosDecl * cosTilt * Math.cos((azimuth - 180) * Math.PI / 180)
                 + sinLat * cosDecl * sinTilt;
  
  const cosZenith = sinLat * sinDecl + cosLat * cosDecl;
  
  const rb = cosZenith > 0.01 ? Math.max(0, cosTheta / cosZenith) : 1.0;
  
  // Calculate incidence angle for IAM
  const incidenceAngle = Math.acos(Math.max(-1, Math.min(1, cosTheta))) * 180 / Math.PI;
  const iam = calculateIAM(incidenceAngle);
  
  // Apply transposition with ground reflection
  const groundReflectance = 0.2;
  const diffuseFraction = 0.3; // Typical for Saudi Arabia
  
  // Apply IAM to beam component (diffuse and reflected are not affected by IAM)
  const beamComponent = ghi * (1 - diffuseFraction) * rb * iam;
  const diffuseComponent = ghi * diffuseFraction * (1 + cosTilt) / 2;
  const reflectedComponent = ghi * groundReflectance * (1 - cosTilt) / 2;
  
  return Math.max(0, beamComponent + diffuseComponent + reflectedComponent);
}

/**
 * Calculate DC array output with temperature and other losses
 */
function calculateArrayOutput(
  poa: number,
  systemSize: number,
  temperature: number,
  windSpeed: number,
  mountingType: string,
  surfaceType: string,
  daysInMonth: number,
  inputs: CalculationInputs
): number {
  // Base production
  let production = poa * systemSize;
  
  // Temperature losses (PV modules lose ~0.4% per °C above 25°C)
  const tempCoefficient = -0.004;
  const referenceTemp = 25;
  
  // Calculate cell temperature using NOCT-based model (more accurate)
  const averageIrradiance = (poa / daysInMonth) * 1000 / 24; // Convert to W/m²
  const cellTemp = calculateCellTemperature(temperature, averageIrradiance, windSpeed, mountingType);
  
  const tempLossFactor = 1 + tempCoefficient * (cellTemp - referenceTemp);
  production *= tempLossFactor;
  
  // Mounting type efficiency
  const mountingEfficiency = getMountingEfficiency(mountingType);
  production *= mountingEfficiency;
  
  // Surface type losses (heat reflection)
  const surfaceEfficiency = getSurfaceEfficiency(surfaceType);
  production *= surfaceEfficiency;
  
  // Mismatch losses (2%)
  production *= 0.98;
  
  // Wiring losses (2%)
  production *= 0.98;
  
  // Soiling losses (smart default based on user type and cleaning schedule)
  // Default values: residential 4%, commercial 5%, industrial 6%, agricultural 10%
  const soilingLossFactor = 1 - (inputs.soilingLoss || getSoilingDefault(inputs.userType || 'residential'));
  production *= soilingLossFactor;
  
  // Module quality losses (2%)
  production *= 0.98;
  
  return Math.max(0, production);
}

/**
 * Calculate AC grid output after inverter
 */
function calculateGridOutput(
  arrayOutput: number,
  inverterSize: number
): number {
  // Inverter efficiency curve (simplified)
  const loadRatio = arrayOutput / (inverterSize * 730); // Approximate hours per month
  
  let inverterEfficiency: number;
  if (loadRatio < 0.05) {
    inverterEfficiency = 0.90;
  } else if (loadRatio < 0.20) {
    inverterEfficiency = 0.95;
  } else if (loadRatio < 0.80) {
    inverterEfficiency = 0.98;
  } else {
    inverterEfficiency = 0.96;
  }
  
  // Apply clipping if DC > inverter capacity
  const maxOutput = inverterSize * 730 * inverterEfficiency; // Max AC output per month
  const gridOutput = Math.min(arrayOutput * inverterEfficiency, maxOutput);
  
  return Math.max(0, gridOutput);
}

/**
 * Get shading loss factor
 */
function getShadingFactor(level: 'none' | 'partial' | 'significant'): number {
  switch (level) {
    case 'none':
      return 1.0;
    case 'partial':
      return 0.875; // 12.5% loss
    case 'significant':
      return 0.75;  // 25% loss
    default:
      return 1.0;
  }
}

/**
 * Get mounting type efficiency factor
 */
function getMountingEfficiency(mountingType: string): number {
  switch (mountingType) {
    case 'ground-fixed':
      return 1.02;
    case 'roof-fixed':
      return 1.01;
    case 'single-axis':
      return 1.04;
    case 'single-axis-backtracking':
      return 1.05;
    case 'dual-axis':
      return 1.07;
    case 'carport-mounted':
      return 1.01;
    default:
      return 1.0;
  }
}

/**
 * Get surface type efficiency factor
 */
function getSurfaceEfficiency(surfaceType: string): number {
  switch (surfaceType) {
    case 'concrete':
      return 0.99;
    case 'metal':
      return 0.98;
    case 'asphalt':
      return 0.97;
    case 'soil':
      return 0.99;
    default:
      return 1.0;
  }
}

/**
 * Get default soiling loss based on user type
 * Based on typical cleaning schedules in Saudi Arabia
 */
function getSoilingDefault(userType: string): number {
  switch (userType) {
    case 'residential':
      return 0.04; // 4% - usually well-maintained with regular cleaning
    case 'commercial':
      return 0.05; // 5% - professional maintenance, cleaned monthly
    case 'industrial':
      return 0.06; // 6% - larger systems, less frequent cleaning
    case 'agricultural':
      return 0.10; // 10% - dusty environment, limited cleaning
    default:
      return 0.04;
  }
}

/**
 * Calculate Incidence Angle Modifier (IAM) using Fresnel reflection model
 * Based on PVSyst methodology for glass-covered modules with AR coating
 */
function calculateIAM(incidenceAngle: number): number {
  // For angles > 80°, reflection is very high
  if (incidenceAngle > 80) {
    return 0.0;
  }
  
  // Optical properties (standard for solar glass with AR coating)
  const n_glass = 1.526;      // Refractive index of glass
  const n_ar = 1.290;         // AR coating refractive index
  const n_air = 1.0;
  
  const theta = incidenceAngle * Math.PI / 180; // Convert to radians
  
  // Snell's law for refraction angles
  const theta_ar = Math.asin(n_air * Math.sin(theta) / n_ar);
  const theta_glass = Math.asin(n_ar * Math.sin(theta_ar) / n_glass);
  
  // Fresnel equations for s and p polarization
  const r_s_air_ar = Math.pow(
    (n_air * Math.cos(theta) - n_ar * Math.cos(theta_ar)) /
    (n_air * Math.cos(theta) + n_ar * Math.cos(theta_ar)),
    2
  );
  
  const r_p_air_ar = Math.pow(
    (n_ar * Math.cos(theta) - n_air * Math.cos(theta_ar)) /
    (n_ar * Math.cos(theta) + n_air * Math.cos(theta_ar)),
    2
  );
  
  const r_s_ar_glass = Math.pow(
    (n_ar * Math.cos(theta_ar) - n_glass * Math.cos(theta_glass)) /
    (n_ar * Math.cos(theta_ar) + n_glass * Math.cos(theta_glass)),
    2
  );
  
  const r_p_ar_glass = Math.pow(
    (n_glass * Math.cos(theta_ar) - n_ar * Math.cos(theta_glass)) /
    (n_glass * Math.cos(theta_ar) + n_ar * Math.cos(theta_glass)),
    2
  );
  
  // Average reflection for unpolarized light
  const reflection = 0.5 * ((r_s_air_ar + r_s_ar_glass) + (r_p_air_ar + r_p_ar_glass));
  
  // IAM is the transmission coefficient
  const iam = 1 - reflection;
  
  return Math.max(0, Math.min(1, iam));
}

/**
 * Calculate cell temperature using NOCT-based model
 * More accurate than simple ambient + offset
 */
function calculateCellTemperature(
  ambientTemp: number,
  irradiance: number,      // W/m²
  windSpeed: number,       // m/s
  mountingType: string
): number {
  // NOCT (Nominal Operating Cell Temperature) - standard for most modules
  const NOCT = 45; // °C
  const NOCT_irradiance = 800; // W/m²
  const NOCT_ambient = 20; // °C
  const NOCT_wind = 1; // m/s
  
  // Mounting factor (affects cooling)
  let mountingFactor = 1.0;
  switch (mountingType) {
    case 'ground-fixed':
      mountingFactor = 0.95; // Better cooling
      break;
    case 'roof-mounted':
      mountingFactor = 1.10; // Worse cooling (heat from roof)
      break;
    case 'building-integrated':
      mountingFactor = 1.20; // Poor cooling
      break;
    case 'carport-mounted':
      mountingFactor = 0.98;
      break;
    default:
      mountingFactor = 1.0;
  }
  
  // Wind cooling effect
  const windFactor = Math.pow(NOCT_wind / Math.max(windSpeed, 0.5), 0.25);
  
  // Calculate cell temperature
  const cellTemp = ambientTemp + 
    ((NOCT - NOCT_ambient) * (irradiance / NOCT_irradiance) * mountingFactor * windFactor);
  
  return cellTemp;
}

/**
 * Calculate system size based on consumption
 */
export async function calculateSystemSizeFromConsumption(
  monthlyConsumption: number[],
  cityWeatherData: CityWeatherData | { latitude: number; longitude: number },
  offsetTarget: number = 1.0 // 100% offset by default
): Promise<number> {
  // If coordinates provided instead of weather data, fetch it
  let weatherData: CityWeatherData;
  if ('monthly' in cityWeatherData) {
    weatherData = cityWeatherData;
    console.log('[solarCalculationEngine] calculateSystemSizeFromConsumption: using provided cityWeatherData', {
      location: weatherData.name || weatherData.id,
      annualGHI: weatherData.monthly.ghi.reduce((s, v) => s + v, 0).toFixed(0),
    });
  } else {
    const result = await getWeatherData({
      latitude: cityWeatherData.latitude,
      longitude: cityWeatherData.longitude,
    });
    weatherData = weatherDataToCityWeatherData(result.data, cityWeatherData.latitude, cityWeatherData.longitude);
    console.log('[solarCalculationEngine] calculateSystemSizeFromConsumption: fetched weather for coordinates', {
      lat: cityWeatherData.latitude,
      lon: cityWeatherData.longitude,
      source: result.source.source,
      sourceMessage: result.source.message,
    });
  }
  const yearlyConsumption = monthlyConsumption.reduce((sum, val) => sum + val, 0);
  const targetProduction = yearlyConsumption * offsetTarget;

  // Estimate specific production for the location
  const annualGHI = weatherData.monthly.ghi.reduce((sum, val) => sum + val, 0);
  const estimatedSpecificProduction = annualGHI * 0.75; // Conservative efficiency

  // Calculate required system size
  const systemSize = targetProduction / estimatedSpecificProduction;

  console.log('[solarCalculationEngine] System size from consumption (weather-based)', {
    annualGHIKwhM2: annualGHI.toFixed(0),
    estimatedSpecificProductionKwhPerKwp: estimatedSpecificProduction.toFixed(0),
    targetProductionKwh: targetProduction.toFixed(0),
    systemSizeKwp: Math.ceil(systemSize * 2) / 2,
  });

  // Round to nearest 0.5 kWp
  return Math.ceil(systemSize * 2) / 2;
}
